import {
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Divider,
  Radio,
  RadioGroup,
  FormControlLabel,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { useController, useFormContext } from "react-hook-form";
import IconButton from "../../../Buttons/IconButton";
import Button from "../../../Buttons/Button";
import { useState, useEffect, useCallback } from "react";
import {
  PRODUCT_SET_AUDIENCE_BUILDER_CUSTOM_DAYS,
  PRODUCT_SET_AUDIENCE_BUILDER_CUSTOM_TITLE,
  PRODUCT_SET_AUDIENCE_BUILDER_CUSTOM_INCLUSIONS_ADD_BUTTON,
  PRODUCT_SET_AUDIENCE_BUILDER_CUSTOM_EXCLUSIONS_TITLE,
  PRODUCT_SET_AUDIENCE_BUILDER_CUSTOM_EXCLUSIONS_ADD_BUTTON,
  PRODUCT_SET_AUDIENCE_BUILDER_CUSTOM_IN_THE_LAST,
  PRODUCT_SET_AUDIENCE_BUILDER_CUSTOM_INCLUSIONS_TITLE,
  PRODUCT_SET_AUDIENCE_BUILDER_TIKTOK_PROSPECTING_SEGMENT,
  PRODUCT_SET_AUDIENCE_BUILDER_TIKTOK_RETARGETING_SEGMENT,
} from "../../../../i18n/keysTranslations";
import { useTranslationApp } from "../../../../lib/i18next";
import _ from "lodash";
import {
  detectPreset,
  PRESETS,
  EVENT_OPTIONS,
  DAYS_OPTIONS,
} from "../../../../services/tiktok";
import { INPUT_COLOR_BORDER } from "../../../../utils/colors";

const RETARGET_CONFIGURATION_KEY = "retarget";
const PROSPECTING_CONFIGURATION_KEY = "prospecting";

/** Canonical empty product-set audience for TikTok catalog (explicit arrays). */
const EMPTY_AUDIENCE = {
  inclusions: [],
  exclusions: [],
};

const isEmptyAudienceValue = (v) =>
  !v ||
  ((v.inclusions?.length ?? 0) === 0 && (v.exclusions?.length ?? 0) === 0);

const defaultCustom = {
  inclusions: [
    { code: "VIEW_PRODUCT", days: 30 },
    { code: "ADD_TO_CART", days: 30 },
  ],
  exclusions: [{ code: "PURCHASE", days: 30 }],
};

const commonSxSelect = {
  width: 70,
  "& .MuiInputBase-input": {
    py: 0,
  },
};

const getAvailableEvents = (currentEvents) => {
  const usedEvents = currentEvents.map((event) => event.code);
  return EVENT_OPTIONS.filter((opt) => !usedEvents.includes(opt.value));
};

const getFirstAvailableEvent = (currentEvents) => {
  const available = getAvailableEvents(currentEvents);
  return available.length > 0 ? available[0].value : null;
};

const isEventDuplicate = (eventCode, list, currentIndex) => {
  return list.some(
    (item, idx) => idx !== currentIndex && item.code === eventCode
  );
};

const hasEventWithSameDays = (eventCode, days, list) => {
  return list.some((item) => item.code === eventCode && item.days === days);
};

/**
 * Catalog product-set audience rules for TikTok.
 *
 * @param {boolean} allowEmptyAudience - When true (Android app), the user may leave
 *   `productSetAudience` empty (prospecting). When false (iOS app), a non-empty
 *   preset is required; the effect below seeds the first preset if the field is empty.
 */
const ProductSetAudienceBuilderTikTok = ({
  name,
  control,
  productSetSelected,
  sxTextField = {},
  allowEmptyAudience = false,
}) => {
  const { t } = useTranslationApp();
  const theme = useTheme();
  const [selectedPreset, setSelectedPreset] = useState(null);

  const { setValue } = useFormContext();
  const {
    field: { value: valueField, onChange: onChangeField },
  } = useController({ control, name });

  // Segment UI is derived from form value: empty => prospecting (Android only), else retarget.
  const isEmptyField = isEmptyAudienceValue(valueField);
  const targetingType =
    allowEmptyAudience && isEmptyField
      ? PROSPECTING_CONFIGURATION_KEY
      : RETARGET_CONFIGURATION_KEY;

  const onPresetChange = useCallback(
    (e, from) => {
      const newPreset = e.target.value;
      setSelectedPreset(newPreset);
      if (newPreset !== "custom") {
        const preset = _.cloneDeep(PRESETS.find((p) => p.key === newPreset));
        const structure = preset?.structure || defaultCustom;

        if (from === "initialize") {
          setValue(name, structure, { shouldDirty: false });
        } else {
          onChangeField(structure);
        }
      } else {
        const isEmpty =
          !valueField ||
          (valueField?.inclusions?.length === 0 &&
            valueField?.exclusions?.length === 0);
        if (isEmpty) {
          if (from === "initialize") {
            setValue(name, defaultCustom, { shouldDirty: false });
          } else {
            onChangeField(defaultCustom);
          }
        }
      }
    },
    [name, onChangeField, setValue, valueField]
  );

  const onAudienceModeChange = useCallback(
    (e) => {
      if (!allowEmptyAudience) return;
      const newType = e.target.value;
      if (newType === PROSPECTING_CONFIGURATION_KEY) {
        setSelectedPreset(null);
        onChangeField(EMPTY_AUDIENCE);
      } else if (isEmptyField) {
        onPresetChange({ target: { value: PRESETS[0].key } });
      }
    },
    [allowEmptyAudience, isEmptyField, onChangeField, onPresetChange]
  );

  /**
   * Keeps local radio state (`selectedPreset`) aligned with the form value without
   * writing in loops: user-driven updates use `onPresetChange` / `onAudienceModeChange`.
   *
   * Branches:
   * - Prospecting (Android + empty audience): clear preset selection; presets are hidden.
   * - iOS (`!allowEmptyAudience`) + empty field: seed first preset once via `setValue`
   *   (`shouldDirty: false`) so the builder matches the mandatory catalog rule.
   * - Retarget + empty field: do nothing (handled by user switching segment or parent).
   * - Retarget + user chose "custom": do not overwrite with `detectPreset`.
   * - Otherwise: sync radio to the structure in the form (`detectPreset`).
   */
  useEffect(() => {
    if (targetingType === PROSPECTING_CONFIGURATION_KEY) {
      if (selectedPreset !== null) {
        setSelectedPreset(null);
      }
      return;
    }

    if (!allowEmptyAudience && isEmptyField) {
      const firstPreset = PRESETS[0];
      if (selectedPreset !== firstPreset.key) {
        setSelectedPreset(firstPreset.key);
      }
      setValue(name, _.cloneDeep(firstPreset.structure), {
        shouldDirty: false,
      });
      return;
    }

    if (selectedPreset === "custom" || isEmptyField) {
      return;
    }

    const detectedPreset = detectPreset(valueField);
    if (selectedPreset !== detectedPreset) {
      setSelectedPreset(detectedPreset);
    }
  }, [
    allowEmptyAudience,
    isEmptyField,
    name,
    selectedPreset,
    setValue,
    targetingType,
    valueField,
  ]);

  const onPresetDaysChange = (presetKey, newDays) => {
    const preset = _.cloneDeep(PRESETS.find((p) => p.key === presetKey));
    if (!preset) return;

    const validDays = DAYS_OPTIONS.includes(Number(newDays))
      ? Number(newDays)
      : 30;

    const newStructure = {
      ...valueField,
      inclusions: preset.structure.inclusions.map((inc) => ({
        ...inc,
        days: validDays,
      })),
      exclusions:
        preset.structure.exclusions?.map((exc) => ({
          ...exc,
          days: validDays,
        })) || [],
    };

    onChangeField(newStructure);
  };

  const onCustomInclusionChange = (idx, key, val) => {
    if (!valueField) return;

    if (key === "code") {
      if (valueField.inclusions.length >= 3 && !valueField.inclusions[idx]) {
        return;
      }
      if (isEventDuplicate(val, valueField.inclusions, idx)) return;
      if (
        hasEventWithSameDays(
          val,
          valueField.inclusions[idx].days,
          valueField.exclusions
        )
      ) {
        return;
      }
    }

    const inclusions = valueField.inclusions.map((inc, i) =>
      i === idx ? { ...inc, [key]: val } : inc
    );

    onChangeField({ ...valueField, inclusions });
  };

  const onCustomExclusionChange = (idx, key, val) => {
    if (!valueField) return;

    if (key === "code") {
      if (valueField.exclusions.length >= 3 && !valueField.exclusions[idx]) {
        return;
      }
      if (isEventDuplicate(val, valueField.exclusions, idx)) return;
      if (
        hasEventWithSameDays(
          val,
          valueField.exclusions[idx].days,
          valueField.inclusions
        )
      ) {
        return;
      }
    }

    const exclusions = valueField.exclusions.map((exc, i) =>
      i === idx ? { ...exc, [key]: val } : exc
    );

    onChangeField({ ...valueField, exclusions });
  };

  const onAddInclusion = () => {
    if (!valueField || valueField.inclusions.length >= 3) return;

    const newEvent = getFirstAvailableEvent(valueField.inclusions);
    if (!newEvent) return;

    if (hasEventWithSameDays(newEvent, 30, valueField.inclusions)) return;

    onChangeField({
      ...valueField,
      inclusions: [...valueField.inclusions, { code: newEvent, days: 30 }],
    });
  };

  const onRemoveInclusion = (idx) => {
    if (!valueField) return;
    onChangeField({
      ...valueField,
      inclusions: valueField.inclusions.filter((_, i) => i !== idx),
    });
  };

  const onAddExclusion = () => {
    if (!valueField || valueField.exclusions.length >= 3) return;

    const newEvent = getFirstAvailableEvent(valueField.exclusions);
    if (!newEvent) return;

    if (hasEventWithSameDays(newEvent, 30, valueField.exclusions)) return;

    onChangeField({
      ...valueField,
      exclusions: [...valueField.exclusions, { code: newEvent, days: 30 }],
    });
  };

  const onRemoveExclusion = (idx) => {
    if (!valueField) return;
    onChangeField({
      ...valueField,
      exclusions: valueField.exclusions.filter((_, i) => i !== idx),
    });
  };

  const renderPreset = (preset) => {
    const isSelected = selectedPreset === preset.key;
    const currentDays =
      isSelected && valueField?.inclusions?.[0]?.days
        ? valueField.inclusions[0].days
        : preset.structure.inclusions[0].days;

    return (
      <Box key={preset.key} sx={{ mb: 2 }}>
        <FormControlLabel
          value={preset.key}
          control={<Radio sx={{ pt: 0.25 }} />}
          sx={{ alignItems: "flex-start" }}
          label={
            <Box>
              <Typography variant="subtitle1">{t(preset.labelKey)}</Typography>
              <Typography
                variant="div"
                color="fields.placeholder"
                sx={{ lineHeight: 2 }}
              >
                {t(preset.descriptionKey, {
                  value: productSetSelected?.name || "",
                })}
                <FormControl size="small" sx={{ mx: 1 }}>
                  <Select
                    value={currentDays}
                    onChange={(e) =>
                      onPresetDaysChange(preset.key, Number(e.target.value))
                    }
                    disabled={!isSelected}
                    sx={{
                      ...commonSxSelect,
                      border: `1px solid ${INPUT_COLOR_BORDER}`,
                      ...sxTextField,
                    }}
                  >
                    {DAYS_OPTIONS.map((d) => (
                      <MenuItem key={d} value={d}>
                        {d}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {t(PRODUCT_SET_AUDIENCE_BUILDER_CUSTOM_DAYS)}
              </Typography>
            </Box>
          }
        />
      </Box>
    );
  };

  const renderCustomInclusion = (inc, idx) => {
    const { code, days } = inc;
    const availableEvents = getAvailableEvents(valueField?.inclusions || []);
    const lengthRules =
      (valueField?.inclusions?.length || 0) +
      (valueField?.exclusions?.length || 0);
    const showRemove = lengthRules >= 2 && valueField?.inclusions?.length >= 2;

    return (
      <Box
        key={idx}
        sx={{ display: "flex", alignItems: "center", mt: 1, mb: 1 }}
      >
        <FormControl size="small" sx={{ mr: 1, width: 150 }}>
          <Select
            value={code}
            onChange={(e) =>
              onCustomInclusionChange(idx, "code", e.target.value)
            }
            sx={{
              border: `1px solid ${INPUT_COLOR_BORDER}`,
              ...sxTextField,
            }}
          >
            {EVENT_OPTIONS.map((opt) => (
              <MenuItem
                key={opt.value}
                value={opt.value}
                disabled={
                  !availableEvents.find((e) => e.value === opt.value) &&
                  opt.value !== code
                }
              >
                {t(opt.labelKey)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="body2" sx={{ mr: 1 }}>
          {t(PRODUCT_SET_AUDIENCE_BUILDER_CUSTOM_IN_THE_LAST)}
        </Typography>
        <FormControl size="small">
          <Select
            value={days}
            onChange={(e) =>
              onCustomInclusionChange(idx, "days", Number(e.target.value))
            }
            sx={{
              ...commonSxSelect,
              mr: 1,
              border: `1px solid ${INPUT_COLOR_BORDER}`,
              ...sxTextField,
            }}
          >
            {DAYS_OPTIONS.map((d) => (
              <MenuItem key={d} value={d}>
                {d}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="body2" sx={{ mr: 1 }}>
          {t(PRODUCT_SET_AUDIENCE_BUILDER_CUSTOM_DAYS)}
        </Typography>
        {showRemove && (
          <IconButton size="small" onClick={() => onRemoveInclusion(idx)}>
            <RemoveCircleOutlineIcon />
          </IconButton>
        )}
      </Box>
    );
  };

  const renderCustomExclusion = (exc, idx) => {
    const { code, days } = exc;
    const availableEvents = getAvailableEvents(valueField?.exclusions || []);
    const lengthRules =
      (valueField?.inclusions?.length || 0) +
      (valueField?.exclusions?.length || 0);
    const showRemove = lengthRules >= 2;

    return (
      <Box key={idx} sx={{ display: "flex", alignItems: "center", mb: 1 }}>
        <FormControl size="small" sx={{ mr: 1, width: 150 }}>
          <Select
            value={code}
            onChange={(e) =>
              onCustomExclusionChange(idx, "code", e.target.value)
            }
            sx={{
              border: `1px solid ${INPUT_COLOR_BORDER}`,
              ...sxTextField,
            }}
          >
            {EVENT_OPTIONS.map((opt) => (
              <MenuItem
                key={opt.value}
                value={opt.value}
                disabled={
                  !availableEvents.find((e) => e.value === opt.value) &&
                  opt.value !== code
                }
              >
                {t(opt.labelKey)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="body2" sx={{ mr: 1 }}>
          {t(PRODUCT_SET_AUDIENCE_BUILDER_CUSTOM_IN_THE_LAST)}
        </Typography>
        <FormControl size="small">
          <Select
            value={days}
            onChange={(e) =>
              onCustomExclusionChange(idx, "days", Number(e.target.value))
            }
            sx={{
              ...commonSxSelect,
              mr: 1,
              border: `1px solid ${INPUT_COLOR_BORDER}`,
              ...sxTextField,
            }}
          >
            {DAYS_OPTIONS.map((d) => (
              <MenuItem key={d} value={d}>
                {d}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="body2" sx={{ mr: 1 }}>
          {t(PRODUCT_SET_AUDIENCE_BUILDER_CUSTOM_DAYS)}
        </Typography>
        {showRemove && (
          <IconButton size="small" onClick={() => onRemoveExclusion(idx)}>
            <RemoveCircleOutlineIcon />
          </IconButton>
        )}
      </Box>
    );
  };

  const isDisabledMode = !allowEmptyAudience;

  const segmentOptions = [
    {
      key: PROSPECTING_CONFIGURATION_KEY,
      label: PRODUCT_SET_AUDIENCE_BUILDER_TIKTOK_PROSPECTING_SEGMENT,
    },
    {
      key: RETARGET_CONFIGURATION_KEY,
      label: PRODUCT_SET_AUDIENCE_BUILDER_TIKTOK_RETARGETING_SEGMENT,
    },
  ];

  return (
    <Box>
      <Box
        sx={{
          display: "inline-flex",
          borderRadius: "8px",
          border: `1px solid ${INPUT_COLOR_BORDER}`,
          overflow: "hidden",
          mb: 2,
          opacity: isDisabledMode ? 0.5 : 1,
          pointerEvents: isDisabledMode ? "none" : "auto",
        }}
      >
        {segmentOptions.map((option) => {
          const isSelected = targetingType === option.key;
          return (
            <Box
              key={option.key}
              onClick={() =>
                onAudienceModeChange({ target: { value: option.key } })
              }
              sx={{
                px: 2,
                py: 1,
                cursor: "pointer",
                userSelect: "none",
                borderRight:
                  option.key === PROSPECTING_CONFIGURATION_KEY
                    ? `1px solid ${INPUT_COLOR_BORDER}`
                    : "none",
                backgroundColor: isSelected
                  ? theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.12)"
                    : "rgba(0,0,0,0.08)"
                  : "transparent",
                transition: "background-color 0.2s",
                "&:hover": {
                  backgroundColor: isSelected
                    ? undefined
                    : theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.06)"
                      : "rgba(0,0,0,0.04)",
                },
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: isSelected ? 600 : 400,
                  color: isSelected ? "text.primary" : "text.secondary",
                }}
              >
                {t(option.label)}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {targetingType === RETARGET_CONFIGURATION_KEY && (
        <FormControl fullWidth component="fieldset">
          <RadioGroup value={selectedPreset || ""} onChange={onPresetChange}>
            {PRESETS.map(renderPreset)}
            <Divider sx={{ my: 2 }} />
            <Box>
              <FormControlLabel
                value="custom"
                control={<Radio sx={{ pt: 0.25 }} />}
                sx={{ alignItems: "flex-start" }}
                label={
                  <Typography variant="subtitle1">
                    {t(PRODUCT_SET_AUDIENCE_BUILDER_CUSTOM_TITLE)}
                  </Typography>
                }
              />
              {selectedPreset === "custom" && valueField && (
                <Box sx={{ pl: 3 }}>
                  <Typography variant="body2" sx={{ my: 1 }}>
                    {t(PRODUCT_SET_AUDIENCE_BUILDER_CUSTOM_INCLUSIONS_TITLE)}
                  </Typography>
                  {valueField.inclusions.map(renderCustomInclusion)}
                  {valueField.inclusions.length < 3 && (
                    <Button
                      color="secondary"
                      startIcon={<AddIcon />}
                      size="small"
                      sx={{ mb: 2, fontSize: 14 }}
                      onClick={onAddInclusion}
                    >
                      {t(
                        PRODUCT_SET_AUDIENCE_BUILDER_CUSTOM_INCLUSIONS_ADD_BUTTON
                      )}
                    </Button>
                  )}
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {t(PRODUCT_SET_AUDIENCE_BUILDER_CUSTOM_EXCLUSIONS_TITLE)}
                  </Typography>
                  {valueField.exclusions.map(renderCustomExclusion)}
                  {valueField.exclusions.length < 3 && (
                    <Button
                      color="secondary"
                      startIcon={<AddIcon />}
                      size="small"
                      sx={{ fontSize: 14 }}
                      onClick={onAddExclusion}
                    >
                      {t(
                        PRODUCT_SET_AUDIENCE_BUILDER_CUSTOM_EXCLUSIONS_ADD_BUTTON
                      )}
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          </RadioGroup>
        </FormControl>
      )}
    </Box>
  );
};

export default ProductSetAudienceBuilderTikTok;
